import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      value: 'angeloneblr@gmail.com',
      link: 'mailto:angeloneblr@gmail.com',
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      value: '+91 99023 00787',
      link: 'tel:+919902300787',
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      value: 'AECS Layout, A Block, Singasandra, Bangalore 560068',
      link: null,
    },
    {
      icon: <Shield size={24} />,
      title: 'Angel One SEBI Reg. No',
      value: '1N2000161534',
      link: null,
    },
  ];

  return (
    <div>
      <section className="py-24 md:py-32" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6"
              style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              data-testid="contact-heading"
            >
              Get in Touch
            </h1>
            <p className="text-base leading-relaxed" style={{ color: '#475569' }}>
              Have questions? We're here to help you start your investment journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, idx) => (
              <div
                key={idx}
                data-testid={`contact-info-${idx}`}
                className="card-hover bg-[#FFFFFF] border border-[rgba(10,15,26,0.05)] p-8 text-center"
              >
                <div className="flex justify-center mb-4" style={{ color: '#FF5722' }}>
                  {info.icon}
                </div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
                >
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-base hover:text-[#FF5722] transition-colors"
                    style={{ color: '#475569' }}
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-base" style={{ color: '#475569' }}>
                    {info.value}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-[#FFFFFF] border border-[rgba(10,15,26,0.05)] p-8 md:p-12">
              <h2
                className="text-2xl sm:text-3xl font-semibold mb-8 text-center"
                style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              >
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#0A0F1A' }}
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] transition-colors"
                      style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#0A0F1A' }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] transition-colors"
                      style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#0A0F1A' }}
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] transition-colors"
                      style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#0A0F1A' }}
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-[#E2E8F0] focus:outline-none focus:border-[#FF5722] transition-colors resize-none"
                      style={{ color: '#0A0F1A', backgroundColor: '#FAFAFA' }}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: isSubmitting ? '#94A3B8' : '#FF5722',
                      color: 'white',
                      fontWeight: 600,
                      padding: '1rem 2rem',
                      border: 'none',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <Send size={20} />}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-6"
            style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
          >
            Prefer Instant Chat?
          </h2>
          <p className="text-base mb-8" style={{ color: '#475569' }}>
            Connect with us on WhatsApp for immediate assistance or use our chatbot
          </p>
          <button
            onClick={() => {
              const phoneNumber = '919902300787';
              const message = encodeURIComponent('Hi! I have a question about opening a Demat account.');
              window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
            }}
            data-testid="whatsapp-cta-button"
            className="btn-primary"
            style={{
              backgroundColor: '#25D366',
              color: 'white',
              fontWeight: 600,
              padding: '1rem 2rem',
              border: 'none',
            }}
          >
            Chat on WhatsApp
          </button>
        </div>
      </section>
    </div>
  );
};

export default Contact;